/**
 * ============================================
 * ALERT HISTORY MODAL
 * ============================================
 */

import { X, Clock, CheckCircle } from 'lucide-react';
import { useAlerts } from '@/context/AlertContext';
import { Button } from '@/components/ui/Button';
import { ALERT_INFO } from '@/config';

interface AlertHistoryProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AlertHistory({ isOpen, onClose }: AlertHistoryProps) {
  const { state } = useAlerts();
  const { alertHistory } = state;

  if (!isOpen) return null;

  const formatDateTime = (date: Date) => {
    return date.toLocaleString([], { 
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit' 
    });
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Alert History</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {alertHistory.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No alert history</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alertHistory.map((alert, index) => {
                const info = ALERT_INFO[alert.type];
                const icon = info?.icon ?? '🚨';
                return (
                  <div key={`${alert.id}-${index}`} className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className={`p-2 rounded-lg ${
                      alert.severity === 'critical' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                    }`}>
                      <span className="text-xl">{icon}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium truncate">{alert.title}</h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          alert.severity === 'critical' 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {alert.severity}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>Started: {formatDateTime(alert.timestamp)}</span>
                        {alert.resolvedAt && (
                          <span className="text-green-400">
                            <CheckCircle className="h-3 w-3 inline mr-1" />
                            Resolved: {formatDateTime(alert.resolvedAt)}
                          </span>
                        )}
                        <span>Duration: {formatDuration(alert.durationMs)}</span>
                      </div>
                    </div>

                    <div className="text-xs px-2 py-1 bg-muted rounded">
                      {alert.environmentName}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            {alertHistory.length} resolved alerts
          </p>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
